import {
    asyncListWithSkipTakeCreate,
    cachedValueCreateWithParamsV2,
    Evaluable,
    evaluateWhenFunction,
    hashFunctionCreate,
    stringReplaceEmptyOrWhiteSpaceValue,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import type {DoppeDtoBlogPost} from '../../../client-server-common/types/doppe-dto-blog-post';
import type {IDoppeViewerApi} from '../../../client-server-common/api/doppe-viewer-api';

/**
 * Creates a cached factory for fetching blog posts async lists.
 * The factory caches async lists by search parameter,
 * so multiple calls with the same params return the same cached list.
 * 
 * @param viewerApiClientGetter - A getter function for the viewer API client
 * @returns A cached value factory for blog posts async lists
 * 
 * @example
 * ```ts
 * const blogPostsAsyncListFactory = useMemo(() => {
 *     return doppeViewerAsyncListFactoryBlogPosts(() => viewerApiClient);
 * }, [viewerApiClient]);
 * 
 * const blogPostsAsyncList = blogPostsAsyncListFactory.getValue({
 *     search: searchText
 * });
 * ```
 */
export function doppeViewerAsyncListFactoryBlogPosts(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryBlogPosts.Params>({
            search: hashFunctionCreate(),
        }),
        paramsNormalizationFunction: params => ({
            ...params,
            search: valueConvertWhenNotNullable(params.search, s =>
                stringReplaceEmptyOrWhiteSpaceValue(s, undefined)
            )
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            return asyncListWithSkipTakeCreate<DoppeDtoBlogPost>({
                fetchFunction: async (skip, take) => {
                    return viewerApiClient.getBlogPosts({
                        skip,
                        take,
                        search: params.search,
                    });
                },
                defaultTake: 30,
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryBlogPosts {
    export interface Params {
        /**
         * Search query to filter blog posts by title.
         */
        search?: string;
    }
}
